import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Temporal } from '@js-temporal/polyfill';
import { useState, type FormEvent } from 'react';

import {
  quoteAllocationQuery,
  setForVenueAccount,
  setJobLineForVenueAccount,
  venueMarginQuery,
  type SupplyModel,
} from '../api/agreements.ts';
import { dayWithYear, quoteQuery } from '../api/quotes.ts';
import { Button } from '../components/button.tsx';
import { EditButton } from '../components/action-buttons.tsx';
import { formatMinorUnits } from '../money.ts';

// CB-REQ VEN-11 at v1.989 (venue slice 4B): what a resale job's quote and job show of the venue's
// invoice. The end client is named on that invoice; a line for the venue's own account carries no
// margin; and someone who sees prices gets an estimate of the invoice, collapsed. Nothing here
// reaches the client's own quote.

/** Who the event is finally for. Shown on a resale quote or job, or wherever one is named. */
export function EndClientField({
  value,
  shown,
  editable,
  saving,
  error,
  onSave,
}: {
  value: string | null;
  shown: boolean;
  editable: boolean;
  saving: boolean;
  error: Error | null;
  onSave: (name: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  if (!shown && !value) return null;

  function submit(event: FormEvent) {
    event.preventDefault();
    onSave(draft.trim() || null);
    setEditing(false);
  }

  return (
    <div className="flex flex-col gap-1.5">
      {editing ? (
        <form onSubmit={submit} className="flex flex-wrap items-center gap-2 text-sm">
          <label className="flex items-center gap-3">
            <span className="text-ink-soft">End client</span>
            <input
              className="input w-64"
              value={draft}
              maxLength={200}
              onChange={(e) => setDraft(e.currentTarget.value)}
            />
          </label>
          <Button type="submit" disabled={saving}>
            Save
          </Button>
          <Button type="button" variant="quiet" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </form>
      ) : (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-ink-soft">End client</span>
          <span>{value ?? 'None'}</span>
          {editable ? (
            <EditButton
              what="the end client"
              onClick={() => {
                setDraft(value ?? '');
                setEditing(true);
              }}
            />
          ) : null}
        </div>
      )}
      {error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {error.message}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Where a line can be for the venue's own account: on resale it carries no trade discount, and on
 * direct bill no commission (VEN-11 at v1.990).
 */
export const takesVenueAccount = (model: SupplyModel) =>
  model === 'resale' || model === 'direct_bill';

/**
 * A tick under a line: charged to the venue for its own account, with no margin. Set on the quote
 * by someone who may manage quotes, or from its job (`jobId`) by someone who may manage jobs.
 */
export function VenueAccountTick({
  quoteId,
  jobId,
  lineId,
  checked,
  editable,
  onChanged,
}: {
  quoteId: string;
  jobId?: string;
  lineId: string;
  checked: boolean;
  editable: boolean;
  onChanged?: () => Promise<unknown>;
}) {
  const queryClient = useQueryClient();
  const set = useMutation({
    mutationFn: async (on: boolean) => {
      if (jobId) await setJobLineForVenueAccount(jobId, lineId, on);
      else await setForVenueAccount(quoteId, lineId, on);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: quoteQuery(quoteId).queryKey });
      await onChanged?.();
    },
  });
  if (!editable) {
    return checked ? (
      <span className="block text-xs font-normal text-muted">Venue’s account</span>
    ) : null;
  }
  return (
    <span className="block text-xs font-normal">
      <label className="inline-flex items-center gap-1.5 text-muted">
        <input
          type="checkbox"
          checked={checked}
          disabled={set.isPending}
          onChange={(e) => set.mutate(e.currentTarget.checked)}
        />
        Venue’s account
      </label>
      {set.isError ? (
        <span role="alert" className="block font-semibold text-accent-2-700">
          {set.error.message}
        </span>
      ) : null}
    </span>
  );
}

/** Above the price line, for someone who sees prices: the venue's invoice, estimated. */
export function VenueMarginPanel({ quoteId }: { quoteId: string }) {
  const margin = useQuery(venueMarginQuery(quoteId));
  const m = margin.data;
  if (!m) return null;
  const money = (minor: string) => formatMinorUnits(minor, m.currency);
  // VEN-11 at v1.990: on direct bill, what you will owe the venue and what you keep.
  if (m.kind === 'direct_bill') {
    return (
      <details className="ml-auto w-full max-w-sm text-sm">
        <summary className="cursor-pointer text-ink-soft">
          Venue commission (est.) · keep {money(m.netMinor)}
        </summary>
        <dl className="mt-2 grid grid-cols-[1fr_auto] gap-x-6 gap-y-1">
          <dt className="text-ink-soft">Sold</dt>
          <dd className="text-right tabular-nums">{money(m.approvedMinor)}</dd>
          <dt className="text-ink-soft">Commission owed</dt>
          <dd className="text-right tabular-nums">−{money(m.commissionMinor)}</dd>
          {m.venueGearShareMinor !== '0' ? (
            <>
              <dt className="text-ink-soft">Venue gear share owed</dt>
              <dd className="text-right tabular-nums">−{money(m.venueGearShareMinor)}</dd>
            </>
          ) : null}
          <dt className="font-semibold">Kept, ex GST</dt>
          <dd className="text-right font-semibold tabular-nums">{money(m.netMinor)}</dd>
        </dl>
      </details>
    );
  }
  return (
    <details className="ml-auto w-full max-w-sm text-sm">
      <summary className="cursor-pointer text-ink-soft">
        Venue margin (est.) · net {money(m.netMinor)}
      </summary>
      <dl className="mt-2 grid grid-cols-[1fr_auto] gap-x-6 gap-y-1">
        <dt className="text-ink-soft">As approved</dt>
        <dd className="text-right tabular-nums">{money(m.approvedMinor)}</dd>
        <dt className="text-ink-soft">Trade discount</dt>
        <dd className="text-right tabular-nums">−{money(m.marginMinor)}</dd>
        {m.venueGearSoldMinor !== '0' ? (
          <>
            <dt className="text-ink-soft">Venue gear sold</dt>
            {/* Paid on to the venue, less the selling service we keep: signed so the column adds. */}
            <dd className="text-right tabular-nums">−{money(m.venueGearSoldMinor)}</dd>
            <dt className="text-ink-soft">Selling service</dt>
            <dd className="text-right tabular-nums">+{money(m.sellingServiceMinor)}</dd>
          </>
        ) : null}
        <dt className="font-semibold">Net to us, ex GST</dt>
        <dd className="text-right font-semibold tabular-nums">{money(m.netMinor)}</dd>
      </dl>
    </details>
  );
}

/**
 * VEN-11 at v1.992: on the venue's own quote, what accepting takes from its complimentary
 * allocation and what is left of the period. Above the price line, behind prices, collapsed.
 */
export function AllocationPanel({ quoteId }: { quoteId: string }) {
  const allocation = useQuery(quoteAllocationQuery(quoteId));
  const a = allocation.data;
  if (!a) return null;
  const money = (minor: string) => formatMinorUnits(minor, a.currency);
  const last = Temporal.PlainDate.from(a.periodEnd).subtract({ days: 1 }).toString();
  return (
    <details className="ml-auto w-full max-w-sm text-sm">
      <summary className="cursor-pointer text-ink-soft">
        Allocation (est.) · −{money(a.drawMinor)}
      </summary>
      <dl className="mt-2 grid grid-cols-[1fr_auto] gap-x-6 gap-y-1">
        <dt className="text-ink-soft">Period</dt>
        <dd className="text-right">{`${dayWithYear(a.periodStart)} – ${dayWithYear(last)}`}</dd>
        <dt className="text-ink-soft">Left now</dt>
        <dd className="text-right tabular-nums">{money(a.leftMinor)}</dd>
        <dt className="text-ink-soft">This quote, ex GST</dt>
        <dd className="text-right tabular-nums">−{money(a.drawMinor)}</dd>
        <dt className="font-semibold">Left after</dt>
        <dd className="text-right font-semibold tabular-nums">{money(a.leftAfterMinor)}</dd>
      </dl>
    </details>
  );
}
